import React, { useState, Fragment, useCallback } from 'react';
import debounce from 'lodash/debounce';
import Form, { Field, ValidMessage, ErrorMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { Box, Flex, Text } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import Button from '@atlaskit/button';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { fetchShipToInPPC } from '@css-xp/ppc-sold-to-address-table/src/queries/fetch-ship-to';
import { CCP_SEARCH_MAX_LIMIT } from '@css-xp/ppc-constants';
import { InfoPanel } from '@css-xp/base';
import { CompanyNameInputForm } from './create-customer-wrapper.styles';

export const COMPANY_NAME_MAX_LENGTH = 240;

const isCompanyNameUnderCharacterLimit = (companyName: string) =>
  companyName ? companyName.length <= COMPANY_NAME_MAX_LENGTH : true;

export type CompanyNameInputProps = {
  transactionAccountId?: string;
  companyName?: string;
  formStages: string[];
  onNext: (companyName: string) => void;
  currentStage?: number;
  showDefaultProgressIndicator?: boolean;
  isSoldToAddressArchivalEnabled?: boolean;
};

const CompanyNameInput = ({
  companyName: defaultCompanyName,
  transactionAccountId,
  formStages,
  onNext,
  currentStage = 0,
  showDefaultProgressIndicator = false,
  isSoldToAddressArchivalEnabled = false,
}: CompanyNameInputProps) => {
  const [companyName, setCompanyName] = useState(defaultCompanyName);
  const [isCompanyNameValid, setIsCompanyNameValid] = useState(false);
  const [isCompanyNameInvalid, setIsCompanyNameInvalid] = useState(false);
  const [fetchingCompaniesByName, setFetchingCompaniesByName] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);

  const onCompanyNameChange = useCallback(
    debounce(async (name: string) => {
      if (fetchingCompaniesByName) {
        return;
      }

      if (!isCompanyNameUnderCharacterLimit(name)) {
        setIsCompanyNameInvalid(true);
        return;
      }

      if (name) {
        setShowErrorMessage(false);
        setFetchingCompaniesByName(true);
        setIsCompanyNameValid(false);
        setIsCompanyNameInvalid(false);

        try {
          const { data } = await ansClient.query({
            query: fetchShipToInPPC,
            fetchPolicy: 'network-only',
            variables: {
              searchParams: {
                companyName: name,
                transactionAccountId,
                ...(isSoldToAddressArchivalEnabled && { isActive: true }),
              },
              pageParams: {
                size: CCP_SEARCH_MAX_LIMIT,
              },
            },
          });

          if (data?.shipToParty?.totalCount === 0) {
            setIsCompanyNameValid(true);
            setIsCompanyNameInvalid(false);
          } else {
            setIsCompanyNameInvalid(true);
            setIsCompanyNameValid(false);
          }
        } catch (error) {
          setShowErrorMessage(true);
        }

        setCompanyName(name);
        setFetchingCompaniesByName(false);
      }
    }, 500),
    []
  );

  const onNextClick = () => {
    onNext(companyName as string);
  };

  return (
    <Form onSubmit={onNextClick}>
      {({ formProps }) => (
        <CompanyNameInputForm {...formProps}>
          {showErrorMessage && <InfoPanel type="error" message={<p>Something went wrong. Please try again.</p>} />}
          <Field
            id="name"
            name="name"
            label="What's the company name?"
            testId="company-name-input-field"
            validate={(value: any) =>
              isCompanyNameUnderCharacterLimit(value)
                ? undefined
                : `Company name can be of maximum ${COMPANY_NAME_MAX_LENGTH} characters`
            }
          >
            {({ fieldProps, error }) => (
              <Fragment>
                <Textfield
                  {...fieldProps}
                  defaultValue={companyName}
                  placeholder="Enter name"
                  elemAfterInput={
                    fetchingCompaniesByName && (
                      <Box paddingInline="space.150">
                        <Spinner />
                      </Box>
                    )
                  }
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    onCompanyNameChange(event.currentTarget.value);
                    fieldProps.onChange(event);
                  }}
                />
                {!error && !fetchingCompaniesByName && isCompanyNameValid && (
                  <ValidMessage>Looking good - let's lock it in</ValidMessage>
                )}
                {!error && !fetchingCompaniesByName && isCompanyNameInvalid && (
                  <Box paddingBlockStart="space.050">
                    <Flex alignItems="center">
                      <WarningIcon primaryColor={token('color.icon.warning')} label="Warning" size="small" />
                      <Text size="small" color="color.text.warning">
                        Heads up - a customer with this name already exists
                      </Text>
                    </Flex>
                  </Box>
                )}
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </Fragment>
            )}
          </Field>
          <Box id="footer">
            <Flex justifyContent="space-between" alignItems="center">
              <Box />
              <ProgressIndicator
                appearance={showDefaultProgressIndicator ? 'default' : 'primary'}
                selectedIndex={currentStage}
                values={formStages}
                testId={showDefaultProgressIndicator ? 'default-progress-indicator' : 'primary-progress-indicator'}
              />
              <Button
                appearance="default"
                type="submit"
                testId="company-name-next-button"
                isDisabled={!companyName || fetchingCompaniesByName}
              >
                Next
              </Button>
            </Flex>
          </Box>
        </CompanyNameInputForm>
      )}
    </Form>
  );
};

export default CompanyNameInput;
